import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { RefreshCw, Search, ChevronRight, ArrowRight, Filter, X } from "lucide-react";
import { api, formatApiErrorDetail } from "../api";
import { PageHeader, Badge, Btn } from "../components/common";

const PAGE_SIZE = 25;

function statusPill(processado) {
  return processado
    ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 uppercase font-semibold">Processado</span>
    : <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 uppercase font-semibold">Pendente</span>;
}

export default function WebhookEvents() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [origens, setOrigens] = useState([]);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ origem: "", event: "", q: "", data_ini: "", data_fim: "" });
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, skip: (page - 1) * PAGE_SIZE };
      if (filters.origem) params.origem = filters.origem;
      if (filters.event) params.event = filters.event;
      if (filters.q) params.q = filters.q;
      if (filters.data_ini) params.data_ini = filters.data_ini;
      if (filters.data_fim) params.data_fim = filters.data_fim;
      const { data } = await api.get("/webhook-events", { params });
      setRows(data.rows || []);
      setTotal(data.total || 0);
      setOrigens(data.origens || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [page, filters.origem]);
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, page, filters.origem, filters.event, filters.q]);

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const totalsByOrigem = useMemo(() => {
    const m = {};
    for (const r of rows) m[r.origem_slug] = (m[r.origem_slug] || 0) + 1;
    return m;
  }, [rows]);

  return (
    <div>
      <PageHeader
        overline="Integracoes e Infra"
        title="Comunicacoes de Webhooks"
        action={
          <div className="flex items-center gap-2">
            <label className="inline-flex items-center gap-1.5 text-xs text-muted2 cursor-pointer">
              <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} data-testid="wh-autorefresh" />
              Auto-atualizar 15s
            </label>
            <Btn variant="ghost" onClick={load} testid="wh-refresh"><RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Atualizar</Btn>
          </div>
        }
      />

      {/* Filtros */}
      <div className="bg-white border border-tint rounded-xl p-4 mb-4" data-testid="wh-filters">
        <div className="flex items-center gap-2 mb-3 text-navy">
          <Filter size={14} /> <span className="text-sm font-semibold">Filtros</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <div>
            <label className="block text-[11px] font-semibold text-muted2 uppercase tracking-wide mb-1">Origem</label>
            <select value={filters.origem} onChange={(e) => { setPage(1); setFilters({ ...filters, origem: e.target.value }); }}
              className="w-full px-3 py-2 border border-tint rounded-lg text-sm" data-testid="wh-filter-origem">
              <option value="">Todas ({total})</option>
              {origens.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-muted2 uppercase tracking-wide mb-1">Evento (contem)</label>
            <input value={filters.event} onChange={(e) => setFilters({ ...filters, event: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && (setPage(1) || load())}
              className="w-full px-3 py-2 border border-tint rounded-lg text-sm" placeholder="ex: tenant.created" data-testid="wh-filter-event" />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-muted2 uppercase tracking-wide mb-1">De</label>
            <input type="date" value={filters.data_ini} onChange={(e) => setFilters({ ...filters, data_ini: e.target.value })}
              className="w-full px-3 py-2 border border-tint rounded-lg text-sm" data-testid="wh-filter-data-ini" />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-muted2 uppercase tracking-wide mb-1">Ate</label>
            <input type="date" value={filters.data_fim} onChange={(e) => setFilters({ ...filters, data_fim: e.target.value })}
              className="w-full px-3 py-2 border border-tint rounded-lg text-sm" data-testid="wh-filter-data-fim" />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-muted2 uppercase tracking-wide mb-1">Procurar (id, trid)</label>
            <input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && (setPage(1) || load())}
              className="w-full px-3 py-2 border border-tint rounded-lg text-sm" placeholder="TX-98, wh-priv..." data-testid="wh-filter-q" />
          </div>
          <div className="flex items-end gap-2">
            <Btn onClick={() => { setPage(1); load(); }} testid="wh-apply-filters"><Search size={14} /> Aplicar</Btn>
            {(filters.origem || filters.event || filters.q || filters.data_ini || filters.data_fim) && (
              <button onClick={() => { setFilters({ origem: "", event: "", q: "", data_ini: "", data_fim: "" }); setPage(1); }}
                data-testid="wh-clear-filters"
                className="text-xs text-muted2 hover:text-navy inline-flex items-center gap-1">
                <X size={12} /> limpar
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white border border-tint rounded-xl overflow-hidden" data-testid="wh-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-tint">
            <tr>
              <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wide font-semibold text-muted2 w-40">Quando</th>
              <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wide font-semibold text-muted2 w-48">Origem</th>
              <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wide font-semibold text-muted2">Destino</th>
              <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wide font-semibold text-muted2">Evento</th>
              <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wide font-semibold text-muted2 w-56">Estado HTTP</th>
              <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wide font-semibold text-muted2 w-28">Estado</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr><td colSpan={7} className="text-center text-muted2 py-10" data-testid="wh-empty">Sem eventos.</td></tr>
            )}
            {rows.map((r) => {
              const open = expanded === r.id;
              return (
                <>
                  <motion.tr key={r.id}
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    className="border-b border-tint hover:bg-slate-50/50 cursor-pointer"
                    onClick={() => setExpanded(open ? null : r.id)}
                    data-testid={`wh-row-${r.id}`}>
                    <td className="px-4 py-2 text-xs text-muted2 whitespace-nowrap">
                      {r.em ? new Date(r.em).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "medium" }) : "-"}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 font-mono uppercase">{r.origem_slug}</span>
                        <span className="font-semibold text-navy">{r.origem}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <div className="inline-flex items-center gap-1.5 text-xs">
                        <ArrowRight size={12} className="text-muted2" />
                        <code className="text-[11px] bg-tint px-1.5 py-0.5 rounded">{r.destino}</code>
                        <span className="text-muted2">({r.destino_plataforma})</span>
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <code className="text-xs bg-slate-100 text-navy px-2 py-0.5 rounded font-mono">{r.event}</code>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex flex-col">
                        <span className={`text-xs font-mono font-bold ${r.http_status >= 400 ? "text-red-600" : "text-emerald-600"}`}>{r.http_status}</span>
                        <span className="text-[10px] text-muted2 leading-tight" title={r.http_status_label}>{r.http_status_label}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2">{statusPill(r.processado)}</td>
                    <td className="px-4 py-2 text-muted2">
                      <ChevronRight size={16} className={`transition-transform ${open ? "rotate-90" : ""}`} />
                    </td>
                  </motion.tr>
                  {open && (
                    <tr className="bg-slate-50/70 border-b border-tint" data-testid={`wh-payload-${r.id}`}>
                      <td colSpan={7} className="px-4 py-3">
                        <div className="grid md:grid-cols-3 gap-3">
                          <div>
                            <div className="text-[10px] uppercase font-semibold text-muted2 tracking-wide mb-1">Idempotency key</div>
                            <code className="text-xs break-all">{r.idempotency_key || "-"}</code>
                          </div>
                          {r.identifier && <div>
                            <div className="text-[10px] uppercase font-semibold text-muted2 tracking-wide mb-1">Identificador</div>
                            <code className="text-xs break-all">{r.identifier}</code>
                          </div>}
                          {r.status && <div>
                            <div className="text-[10px] uppercase font-semibold text-muted2 tracking-wide mb-1">Status externo</div>
                            <Badge value={r.status} />
                          </div>}
                        </div>
                        <div className="mt-3">
                          <div className="text-[10px] uppercase font-semibold text-muted2 tracking-wide mb-1">Payload</div>
                          <pre className="text-[11px] bg-navy text-white/90 p-3 rounded overflow-x-auto max-h-64">{JSON.stringify(r.payload, null, 2)}</pre>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Paginacao */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between mt-4 text-sm" data-testid="wh-pagination">
          <div className="text-muted2">
            Pagina {page} de {pages} - {total} eventos
            {Object.keys(totalsByOrigem).length > 0 && (
              <span className="ml-3 text-xs">
                {Object.entries(totalsByOrigem).map(([slug, n]) => <span key={slug} className="mr-2">{slug}: <b>{n}</b></span>)}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Btn variant="ghost" onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} testid="wh-prev">Anterior</Btn>
            <Btn variant="ghost" onClick={() => setPage(Math.min(pages, page + 1))} disabled={page === pages} testid="wh-next">Seguinte</Btn>
          </div>
        </div>
      )}
    </div>
  );
}
